import { useEffect, useRef, useCallback } from 'react';
import { useOfflineStore } from '../stores/offlineStore';
import { processQueue, refreshPendingCount } from '../services/syncService';

const HEALTH_CHECK_INTERVAL = 30000; // 30 seconds
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

/**
 * Hook that monitors network status using browser events and periodic health checks.
 * Updates the offlineStore and returns { isOnline, wasOffline }.
 * `wasOffline` is true for one render cycle after transitioning from offline -> online.
 */
export function useNetworkStatus() {
  const { isOnline, setOnline, setOffline } = useOfflineStore();
  const wasOfflineRef = useRef(false);
  const healthCheckRef = useRef(null);

  const checkConnectivity = useCallback(async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      await fetch(`${API_URL}/health`, {
        method: 'HEAD',
        cache: 'no-store',
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      // We're online
      const wasOfflineBefore = !useOfflineStore.getState().isOnline;
      if (wasOfflineBefore) {
        wasOfflineRef.current = true;
        setOnline();
        // Directly trigger sync queue processing on health-check reconnect
        console.log('[useNetworkStatus] Health check: back online, processing sync queue...');
        processQueue().catch((err) =>
          console.error('Error processing sync queue on health-check reconnect:', err)
        );
      }
    } catch {
      // Network unreachable or server down
      if (useOfflineStore.getState().isOnline) {
        setOffline();
      }
    }
  }, [setOnline, setOffline]);

  useEffect(() => {
    const handleOnline = () => {
      wasOfflineRef.current = true;
      setOnline();
      // Verify with actual connectivity check
      checkConnectivity();
    };

    const handleOffline = () => {
      setOffline();
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Set initial state
    if (!navigator.onLine) {
      setOffline();
    }

    // Periodic health check (navigator.onLine can be unreliable)
    healthCheckRef.current = setInterval(checkConnectivity, HEALTH_CHECK_INTERVAL);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (healthCheckRef.current) {
        clearInterval(healthCheckRef.current);
      }
    };
  }, [setOnline, setOffline, checkConnectivity]);

  // When transitioning back online, trigger sync queue processing
  useEffect(() => {
    if (wasOfflineRef.current && isOnline) {
      // Process any queued items
      processQueue().catch((err) =>
        console.error('Error processing sync queue on reconnect:', err)
      );

      // Reset wasOffline after consumers have seen it
      const timer = setTimeout(() => {
        wasOfflineRef.current = false;
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isOnline]);

  // On mount, refresh the pending count from IndexedDB and auto-process if online
  useEffect(() => {
    refreshPendingCount().then((count) => {
      if (count > 0 && navigator.onLine) {
        console.log(`[useNetworkStatus] Mount: ${count} pending items found, processing queue...`);
        processQueue().catch((err) =>
          console.error('Error processing sync queue on mount:', err)
        );
      }
    }).catch(() => {});
  }, []);

  return {
    isOnline,
    wasOffline: wasOfflineRef.current && isOnline
  };
}
